import { useMemo } from "react";
import { TrendPoint } from "../features/performance/types/PerformanceTypes";
import { useFicheFull, useRegionOverview, useRegionReviews } from "./useGBPQueries";

interface Params {
  locationName?: string;
  accountName?:  string;
  qs:            string;
  qsCompare:     string | null;
  hasLocation:   boolean;
  isRegion:      boolean;
  activeTab:     string;
  subjectEmail?: string; 
}

export const normalizeReviews = (raw: any) => ({
  averageRating: raw?.averageRating != null ? parseFloat(raw.averageRating) : null,
  totalCount:    raw?.totalCount ?? 0,
  distribution:  raw?.distribution ?? { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
  reviews:       raw?.list ?? raw?.reviews ?? [],
});

const buildRegionData = (d: any) => {
  if (!d?.kpis || !d?.series) return null;

  const s   = d.series as Record<string, { date: string; value: number }[]>;
  const sum = (k: string) =>
    (s[k] || []).reduce((a: number, p: { value: number }) => a + p.value, 0);

  const desktop = sum("BUSINESS_IMPRESSIONS_DESKTOP_MAPS")
                + sum("BUSINESS_IMPRESSIONS_DESKTOP_SEARCH");
  const mobile  = sum("BUSINESS_IMPRESSIONS_MOBILE_MAPS")
                + sum("BUSINESS_IMPRESSIONS_MOBILE_SEARCH");
  const total   = desktop + mobile || 1;

  const days: Record<string, Record<string, number>> = {};
  Object.entries(s).forEach(([metric, points]) =>
    (points as any[]).forEach(({ date, value }) => {
      days[date] = days[date] ?? {};
      days[date][metric] = value;
    })
  );

  return {
    overviewData: { kpis: d.kpis, series: d.series },
    trafficData: {
      breakdown: {
        maps:   sum("BUSINESS_IMPRESSIONS_DESKTOP_MAPS")   + sum("BUSINESS_IMPRESSIONS_MOBILE_MAPS"),
        search: sum("BUSINESS_IMPRESSIONS_DESKTOP_SEARCH") + sum("BUSINESS_IMPRESSIONS_MOBILE_SEARCH"),
      },
      detailed: {
        desktopMaps:   sum("BUSINESS_IMPRESSIONS_DESKTOP_MAPS"),
        desktopSearch: sum("BUSINESS_IMPRESSIONS_DESKTOP_SEARCH"),
        mobileMaps:    sum("BUSINESS_IMPRESSIONS_MOBILE_MAPS"),
        mobileSearch:  sum("BUSINESS_IMPRESSIONS_MOBILE_SEARCH"),
      },
      series: d.series,
    },
    devicesData: [
      { name: "Desktop", value: desktop, percentage: Math.round((desktop / total) * 100) },
      { name: "Mobile",  value: mobile,  percentage: Math.round((mobile  / total) * 100) },
    ],
    actionsData: {
      totals: {
        callClicks:        d.kpis.callClicks        ?? 0,
        websiteClicks:     d.kpis.websiteClicks     ?? 0,
        directionRequests: d.kpis.directionRequests ?? 0,
        bookings:          d.kpis.bookings          ?? 0,
        conversations:     d.kpis.conversations     ?? 0,
        foodOrders:        0,
      },
      series: d.series,
    },
    trendsData: Object.entries(days)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, vals]) => ({ date, ...vals } as TrendPoint)),
  };
};

const buildFicheData = (d: any) => {
  if (!d?.kpis) return null;

  const ts = d.trafficSources ?? {};
  return {
    trafficData: {
      breakdown: {
        maps:   ts.maps   ?? 0,
        search: ts.search ?? 0,
      },
      detailed: {
        desktopMaps:   ts.detail?.desktopMaps   ?? 0,
        desktopSearch: ts.detail?.desktopSearch ?? 0,
        mobileMaps:    ts.detail?.mobileMaps    ?? 0,
        mobileSearch:  ts.detail?.mobileSearch  ?? 0,
      },
      series: d.series,
    },
    actionsData: {
      totals: {
        callClicks:        d.kpis.callClicks        ?? 0,
        websiteClicks:     d.kpis.websiteClicks     ?? 0,
        directionRequests: d.kpis.directionRequests ?? 0,
        bookings:          d.kpis.bookings          ?? 0,
        conversations:     d.kpis.conversations     ?? 0,
        foodOrders:        d.kpis.foodOrders        ?? 0,
      },
      series: d.series,
    },
    reviewsData: normalizeReviews(d.reviews),
  };
};

export const useGBPData = ({
  locationName,
  accountName,
  qs,
  qsCompare,
  hasLocation,
  isRegion,
  activeTab,
}: Params) => {
  const isOverviewTab = activeTab !== "reviews"; // tout sauf reviews charge overview
  const isReviewsTab  = activeTab === "reviews";

  // ── Params de base ────────────────────────────────────────────────────────
  const baseParams = {
    qs,
    locationName,
    isRegion,
    enabled: hasLocation && !!qs,
  };

  // ── Params comparaison ────────────────────────────────────────────────────
  const compareParams = {
    qs:          qsCompare ?? "",
    locationName,
    isRegion,
    enabled: hasLocation && !!qsCompare,
  };

  // ── Queries ───────────────────────────────────────────────────────────────
  const ficheFull       = useFicheFull({ ...baseParams,    enabled: hasLocation && !!qs && !isRegion && !!locationName });
  const ficheFull2      = useFicheFull({ ...compareParams, enabled: hasLocation && !!qsCompare && !isRegion && !!locationName });

  const regionOverview  = useRegionOverview({ ...baseParams,    enabled: hasLocation && !!qs && isRegion && isOverviewTab });
  const regionOverview2 = useRegionOverview({ ...compareParams, enabled: hasLocation && !!qsCompare && isRegion && isOverviewTab });

  const regionReviews   = useRegionReviews({ ...baseParams, enabled: hasLocation && !!qs && isRegion && isReviewsTab });

  // ── Computed data ─────────────────────────────────────────────────────────
  const regionData  = useMemo(() => buildRegionData(regionOverview.data),  [regionOverview.data]);
  const regionData2 = useMemo(() => buildRegionData(regionOverview2.data), [regionOverview2.data]);
  const ficheData   = useMemo(() => buildFicheData(ficheFull.data),        [ficheFull.data]);
  const ficheData2  = useMemo(() => buildFicheData(ficheFull2.data),       [ficheFull2.data]);

  return {
    ficheFull,
    ficheFull2,
    regionOverview,
    regionOverview2,
    regionReviews,
    regionData,
    regionData2,
    ficheData,
    ficheData2,
  };
};